import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectHitlExpressTimerMaxSeconds,
  selectIsHitl7Enabled,
} from '../redux/features/config/config.selector';
import {
  selectIsExpressTimerElapsed,
  selectTaskAssignedSessionId,
  selectTaskAssignedSessionStartedAt,
} from '../redux/features/taskRouter/taskRouter.selector';
import { setExpressTimerElapsed } from '../redux/features/taskRouter/taskRouter.slice';
import {
  selectIsCarExited,
  selectSessionId,
} from '../redux/features/sessionBoundary/sessionBoundary.selectors';
import logger from '../utils/logger';

/**
 * HITL-1359 (Phase 2): when the order's elapsed time (measured from the
 * task_assigned `session_started_at`) crosses the `hitl_express_timer`
 * `max_seconds` limit, latch `isExpressTimerElapsed` so the cart logic enters
 * express mode and suppresses `do_not_upsell_in_express_mode`-tagged upsells
 * for the rest of the order.
 *
 * Gated by hitl_3_0 + hitl_express_timer. Mounted in MainPageLayout (new UX),
 * so hitl_3_0 is already implied; the flag/maxSeconds guards keep it inert
 * otherwise. Uses a single setTimeout for the exact remaining time rather than
 * polling — the latch only needs to flip once. The reset is handled by the
 * taskRouter `assigned` reducer on the next task assignment.
 */
const useExpressTimer = () => {
  const dispatch = useAppDispatch();
  const isHitl3 = useSelector(selectFeatureFlagHitl3);
  const isEnabled = useSelector(selectFeatureFlagHitlExpressTimer);
  const maxSeconds = useSelector(selectHitlExpressTimerMaxSeconds);
  const sessionStartedAt = useSelector(selectTaskAssignedSessionStartedAt);
  const alreadyElapsed = useSelector(selectIsExpressTimerElapsed);
  // Car exit ends the order — drop the express-timer latch (and its header bolt)
  // the moment the car leaves rather than waiting for the next task assignment.
  const carExited = useSelector(selectIsCarExited);
  const isHitl7 = useSelector(selectIsHitl7Enabled);
  const sessionId = useSelector(selectSessionId);
  // The session `sessionStartedAt` was issued for. taskRouter writes the two
  // together on task_assigned and refreshes them only on the NEXT assignment,
  // so when one assignment spans several cars the anchor outlives its session.
  const anchorSessionId = useSelector(selectTaskAssignedSessionId);

  // An anchor from a previous session is not an anchor. Without this the reset
  // below is pointless: clearing the latch on a new car just lets this hook
  // re-latch from car 1's start time on its very next run, and express mode
  // lands on a customer who has been at the board for two seconds. Same guard
  // as the header <Timer/>, which owns the latch under 7.0.
  const anchorMatchesSession = !isHitl7 || anchorSessionId === sessionId;

  // HITL-1663: a new customer never inherits the previous order's express mode.
  //
  // Today the latch is cleared in two places, and a car can slip between both:
  // the next task_assigned (which does not fire when one assignment covers
  // several cars) and the car-exit effect below (which needs a car_exit event
  // that a dropped socket can swallow). A change of sessionId is the one signal
  // that is always present when a new customer arrives, so clear on that too —
  // the same point at which the AI's own session signals reset.
  //
  // Guarded on the ref rather than running whenever `alreadyElapsed` flips, so
  // this only ever fires on a session CHANGE and never fights the latch being
  // set mid-order.
  const latchedSessionRef = useRef(sessionId);
  useEffect(() => {
    if (sessionId === latchedSessionRef.current) return;
    latchedSessionRef.current = sessionId;
    if (isHitl7 && alreadyElapsed) {
      logger.debug('[EXPRESS_TIMER] new session — resetting express mode');
      dispatch(setExpressTimerElapsed(false));
    }
  }, [sessionId, isHitl7, alreadyElapsed, dispatch]);

  // Drop the express-timer latch (and its header bolt) on car exit rather than
  // waiting for the next task assignment to clear it.
  useEffect(() => {
    if (carExited && alreadyElapsed) {
      logger.debug('[EXPRESS_TIMER] car exit — resetting express mode');
      dispatch(setExpressTimerElapsed(false));
    }
  }, [carExited, alreadyElapsed, dispatch]);

  useEffect(() => {
    if (
      !isHitl3 ||
      !isEnabled ||
      maxSeconds <= 0 ||
      !sessionStartedAt ||
      !anchorMatchesSession ||
      alreadyElapsed ||
      // Car exited: the order is ending and the stale session_started_at is not
      // cleared until the next assignment. Without this guard, re-latching here
      // fights the car-exit reset effect above in an infinite render loop.
      carExited
    ) {
      return;
    }

    const start = new Date(sessionStartedAt).getTime();
    if (isNaN(start)) {
      return;
    }

    const remainingMs = maxSeconds * 1000 - (new Date().getTime() - start);
    if (remainingMs <= 0) {
      logger.debug(
        `[EXPRESS_TIMER] elapsed on assignment (started ${sessionStartedAt}, limit ${maxSeconds}s) — entering express mode`
      );
      dispatch(setExpressTimerElapsed(true));
      return;
    }

    const timer = setTimeout(() => {
      logger.debug(
        `[EXPRESS_TIMER] limit ${maxSeconds}s reached (started ${sessionStartedAt}) — entering express mode`
      );
      dispatch(setExpressTimerElapsed(true));
    }, remainingMs);

    return () => {
      clearTimeout(timer);
    };
  }, [
    isHitl3,
    isEnabled,
    maxSeconds,
    sessionStartedAt,
    anchorMatchesSession,
    alreadyElapsed,
    carExited,
    dispatch,
  ]);
};

export default useExpressTimer;
